import { Suspense, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Divider, HStack, VStack } from '@cbhq/cds-web/layout';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { InAppErrorMessageFallback } from '../../../components/ErrorBoundary/InAppErrorMessageFallback';
import { teamAccessMessages } from '../../../components/ErrorBoundary/messages';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { getApprovalActionLabel } from '../../../utils/approvalActionLabels';
import { getApprovalProductLabel } from '../../../utils/approvalProductLabels';
import { UnsavedChangesAlert } from '../../Settings/components/UnsavedChangesAlert';
import { useApprovalRuleEdits } from '../hooks/useApprovalRuleEdits';
import { useApprovalRulesData } from '../hooks/useApprovalRulesData';
import { useApprovalRulesSubmission } from '../hooks/useApprovalRulesSubmission';
import { ROUTE_SECURITY } from '../utils/constants';

import { ApprovalProductSection } from './components/ApprovalProductSection';
import { ApprovalRulesSaveBar } from './components/ApprovalRulesSaveBar';
import { ApprovalRulesScope } from './components/ApprovalRulesScope';
import { ApprovalRulesSkeleton } from './components/ApprovalRulesSkeleton';

const messages = defineMessages({
  backLink: {
    id: 'Security.approvalRules.backLink',
    defaultMessage: 'Security',
    description: 'Back link label from the approval rules page to the Security page',
  },
  backLinkAccessibilityLabel: {
    id: 'Security.approvalRules.backLinkAccessibilityLabel',
    defaultMessage: 'Back to Security',
    description: 'Accessibility label for navigating back to the Security page',
  },
  title: {
    id: 'Security.approvalRules.title',
    defaultMessage: 'Approval rules',
    description: 'Title for the workspace Approval rules page',
  },
  noOrgSelectedTitle: {
    id: 'Security.approvalRules.noOrgSelectedTitle',
    defaultMessage: 'No organization selected',
    description: 'Title shown when no organization is selected on the Approval rules page',
  },
  noOrgSelectedDescription: {
    id: 'Security.approvalRules.noOrgSelectedDescription',
    defaultMessage: 'Select an organization to manage approval rules.',
    description: 'Description shown when no organization is selected on the Approval rules page',
  },
  description: {
    id: 'Security.approvalRules.description',
    defaultMessage: 'Require approvals for sensitive actions (eg. transfers, changes)',
    description: 'Description shown on the Approval rules page',
  },
  saveSuccess: {
    id: 'Security.approvalRules.saveSuccess',
    defaultMessage: 'Approval rules updated',
    description: 'Toast shown after approval rule changes are saved',
  },
  saveError: {
    id: 'Security.approvalRules.saveError',
    defaultMessage: 'Unable to save approval rules',
    description: 'Toast shown when approval rule changes cannot be saved',
  },
  savePendingApproval: {
    id: 'Security.approvalRules.savePendingApproval',
    defaultMessage: 'Approval rule changes submitted for approval',
    description: 'Toast shown when approval rule changes require guardian approval',
  },
});

type ApprovalRulesStatus = {
  hasChanges: boolean;
  isSaving: boolean;
};

type ApprovalRulesContentProps = {
  onStateChange: (status: ApprovalRulesStatus) => void;
};

function ApprovalRulesContent({ onStateChange }: ApprovalRulesContentProps) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { selectedOrganization } = useOrganization();
  const legalEntities = selectedOrganization?.legalEntities ?? [];
  const showScope = legalEntities.length > 1;

  const [selectedLegalEntityId, setSelectedLegalEntityId] = useState(
    legalEntities[0]?.legalEntityId ?? '',
  );
  const { approvalProducts, members, refresh } = useApprovalRulesData({
    legalEntityId: selectedLegalEntityId,
  });
  const {
    ruleStateById,
    maxRequiredApprovalsByRuleId,
    changedRules,
    hasChanges,
    canSave,
    handleToggle,
    handleApproversChange,
    handleCountChange,
    resetEdits,
  } = useApprovalRuleEdits(approvalProducts, members);

  const handleSaveSuccess = useCallback(
    (hasPendingApproval: boolean) => {
      const toastMessage = hasPendingApproval ? messages.savePendingApproval : messages.saveSuccess;
      toast.show(formatMessage(toastMessage));
      resetEdits();
      void refresh();
    },
    [formatMessage, refresh, resetEdits, toast],
  );

  const handleSaveFailure = useCallback(() => {
    toast.show(formatMessage(messages.saveError), { variant: 'bgNegative' });
    void refresh();
  }, [formatMessage, refresh, toast]);

  const { isSaving, saveChanges } = useApprovalRulesSubmission({
    changedRules,
    canSave,
    formatMessage,
    onSuccess: handleSaveSuccess,
    onFailure: handleSaveFailure,
  });

  useEffect(() => {
    onStateChange({ hasChanges, isSaving });
  }, [hasChanges, isSaving, onStateChange]);

  const handleLegalEntityChange = useCallback(
    (legalEntityId: string) => {
      setSelectedLegalEntityId(legalEntityId);
      resetEdits();
    },
    [resetEdits],
  );

  return (
    <>
      <VStack gap={6} paddingBottom={10} width="100%">
        {showScope ? (
          <ApprovalRulesScope
            legalEntities={legalEntities}
            selectedLegalEntityId={selectedLegalEntityId}
            onLegalEntityChange={handleLegalEntityChange}
            disabled={isSaving}
          />
        ) : null}
        {approvalProducts.map((product, index) => (
          <VStack key={product.product} gap={6} width="100%">
            <ApprovalProductSection
              productName={getApprovalProductLabel(product.product, formatMessage)}
              showColumnHeaders={index === 0}
              rules={product.rules.map((rule) => ({
                id: rule.id,
                ruleName: getApprovalActionLabel(rule.action, formatMessage),
                maxRequiredApprovals: maxRequiredApprovalsByRuleId[rule.id] ?? 0,
                approverOptions: rule.approverOptions,
                ...ruleStateById[rule.id],
                disabled: isSaving,
                onToggle: () => handleToggle(rule.id),
                onApproversChange: (value) => handleApproversChange(rule.id, value),
                onCountChange: (count) => handleCountChange(rule.id, count),
              }))}
            />
            {index < approvalProducts.length - 1 ? <Divider /> : null}
          </VStack>
        ))}
      </VStack>
      <ApprovalRulesSaveBar
        hasChanges={hasChanges}
        saving={isSaving}
        canSave={canSave}
        onCancel={resetEdits}
        onSave={saveChanges}
      />
    </>
  );
}

export function ApprovalRulesPage() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { selectedOrganization, hasRequestedOrganizationAccessDenied } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  const [approvalRulesStatus, setApprovalRulesStatus] = useState<ApprovalRulesStatus>({
    hasChanges: false,
    isSaving: false,
  });
  const [isBackAlertVisible, setIsBackAlertVisible] = useState(false);
  const { hasChanges, isSaving } = approvalRulesStatus;

  const handleBackClick = useCallback(() => {
    if (isSaving) {
      return;
    }

    if (hasChanges) {
      setIsBackAlertVisible(true);
      return;
    }

    navigate(ROUTE_SECURITY);
  }, [hasChanges, isSaving, navigate]);

  const handleKeepEditing = useCallback(() => {
    setIsBackAlertVisible(false);
  }, []);

  const handleDiscardAndNavigate = useCallback(() => {
    setIsBackAlertVisible(false);
    navigate(ROUTE_SECURITY);
  }, [navigate]);

  if (hasRequestedOrganizationAccessDenied) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(teamAccessMessages.deniedTitle)}
        description={formatMessage(teamAccessMessages.deniedDescription)}
      />
    );
  }

  if (!organizationId) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(messages.noOrgSelectedTitle)}
        description={formatMessage(messages.noOrgSelectedDescription)}
      />
    );
  }

  return (
    <VStack
      className="workspaceContentFadeIn"
      gap={6}
      width="100%"
      data-testid="approval-rules-page"
    >
      <VStack gap={4} width="100%">
        <Pressable
          background="transparent"
          noScaleOnPress
          onClick={handleBackClick}
          disabled={isSaving}
          accessibilityLabel={formatMessage(messages.backLinkAccessibilityLabel)}
        >
          <HStack alignItems="center" gap={2}>
            <Icon name="caretLeft" size="s" color="fgMuted" />
            <Text as="span" font="label1" color="fgMuted">
              {formatMessage(messages.backLink)}
            </Text>
          </HStack>
        </Pressable>
        <VStack gap={1} width="100%">
          <Text as="h1" font="title1">
            {formatMessage(messages.title)}
          </Text>
          <Text font="label2" color="fgMuted">
            {formatMessage(messages.description)}
          </Text>
        </VStack>
      </VStack>

      <Suspense fallback={<ApprovalRulesSkeleton />}>
        <ApprovalRulesContent key={organizationId} onStateChange={setApprovalRulesStatus} />
      </Suspense>
      <UnsavedChangesAlert
        visible={isBackAlertVisible}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscardAndNavigate}
      />
    </VStack>
  );
}
